import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, defer } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoadingSpinnerShellService } from './loading-spinner-shell.service';

/**
 * Single global loading indicator (ngx-spinner in app root).
 * - HTTP calls are tracked automatically via {@link LoadingInterceptor}.
 * - For work that does not use HttpClient, call {@link show} / {@link hide} or {@link run}.
 * - Logged-in shell: full-viewport overlay (fixed) so long pages stay masked while scrolling.
 */
@Injectable({ providedIn: 'root' })
export class GlobalLoadingService {
  private pending = 0;
  /** Spinner name used for the current ref-count stack (snapshot at first begin). */
  private shownSpinnerName: string | null = null;

  constructor(
    private readonly spinner: NgxSpinnerService,
    private readonly shell: LoadingSpinnerShellService
  ) {}

  /** Start tracking one async operation (shows spinner when count goes from 0 → 1). */
  begin(): void {
    this.pending++;
    if (this.pending === 1) {
      const name = this.shell.activeName;
      this.shownSpinnerName = name;
      this.spinner.show(name, { fullScreen: true });
    }
  }

  /** Finish one async operation (hides spinner when count returns to 0). */
  end(): void {
    this.pending = Math.max(0, this.pending - 1);
    if (this.pending === 0) {
      const name = this.shownSpinnerName ?? this.shell.activeName;
      this.shownSpinnerName = null;
      this.spinner.hide(name);
    }
  }

  /** Manual show (pair with {@link hide}). Nested calls are ref-counted. */
  show(): void {
    this.begin();
  }

  /** Manual hide (pair with {@link show}). */
  hide(): void {
    this.end();
  }

  /** Wrap an observable: show until it completes, errors, or unsubscribes. Does not stack with HTTP interceptor for the same request. */
  run<T>(source: Observable<T>): Observable<T> {
    return defer(() => {
      this.show();
      return source.pipe(finalize(() => this.hide()));
    });
  }

  /**
   * When the app switches guest ↔ main layout while requests are still in flight,
   * move the visible spinner to the mounted instance (DOM swaps spinners by name).
   */
  reconcileShell(): void {
    if (this.pending <= 0 || !this.shownSpinnerName) {
      return;
    }
    const next = this.shell.activeName;
    if (next === this.shownSpinnerName) {
      return;
    }
    this.spinner.hide(this.shownSpinnerName);
    this.shownSpinnerName = next;
    this.spinner.show(next, { fullScreen: true });
  }
}
